import { Component, Output, EventEmitter, OnInit, Input, inject } from "@angular/core";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { TestPeriodDataService } from "./adv-eff-testperiod-data.service";
import { TestPeriodModel } from "../shared/test-period.model";
import { BsModalRef } from "ngx-bootstrap/modal";
import { TestSamplesModel } from "../../shared/models/testsamples-model";
import { nameof } from "@methods/jeffs-toolkit";
import { getAssessmentScores } from "../../shared/adv-eff-shared-methods";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { ControlDataService } from "@app/controls/services/control.data.service";
import { CerrixPromptComponent } from "@app/shared/cerrix-prompt/cerrix-prompt.component";
import { ToastrService } from "ngx-toastr";
import { ControlPermissionModel } from "@app/controls/models/ControlPermissionsModel";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";
import { firstValueFrom } from "rxjs";
import { SharedModule } from "@app/shared/shared.module";

@Component({
    selector: "adv-eff-testperiod-manager",
    templateUrl: "./adv-eff-testperiod-manager.component.html",
    styleUrls: ["./adv-eff-testperiod-manager.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [SharedModule, TranslatePipe],
})
export class TestperiodManagerComponent implements OnInit {
    private ds = inject(TestPeriodDataService);
    private _promptService = inject(CerrixPromptService);
    private controlDataService = inject(ControlDataService);
    private _toastrService = inject(ToastrService);
    private _translation = injectTranslation(scope);

    @Input() controlGuid: string;
    @Input() modalRef: BsModalRef;
    @Input() controlPermissions: ControlPermissionModel;

    @Output() reload = new EventEmitter();

    config: GenericListConfig;
    samples: TestSamplesModel;

    async ngOnInit() {
        await this._translation.ready();

        this.ds.getNumberOfTestSamples(this.controlGuid).subscribe((ts) => {
            this.samples = ts;
            this.createConfig();
        });
    }

    createConfig() {
        this.config = <GenericListConfig>{
            name: this._translation.t("testperiodManager.listName"),
            hideHeader: true,
            isSortable: true,

            allowAdd: this.controlPermissions.CanManageEffectivenesses,
            allowEdit: this.controlPermissions.CanManageEffectivenesses,
            allowDelete: this.controlPermissions.CanManageEffectivenesses,

            fields: [
                {
                    fieldName: nameof<TestPeriodModel>((period) => period.Name),
                    prettyName: this._translation.t("testperiodManager.colPeriodName"),
                    fieldType: GenericListFieldType.Text,
                    required: true,
                    editorWidth: 12,
                },
                {
                    fieldName: nameof<TestPeriodModel>((period) => period.minimalSamplesCount),
                    prettyName: this._translation.t("testperiodManager.colMinimalSamples"),
                    fieldType: GenericListFieldType.Number,
                    defaultValue: this.samples.suggestedSamples,
                    minimumValue: this.samples.minimumSamples,
                    editorWidth: "12",
                },
                {
                    fieldName: nameof<TestPeriodModel>((period) => period.effectivenessPeriodCount),
                    prettyName: this._translation.t("testperiodManager.colEffectivenessPeriods"),
                    fieldType: GenericListFieldType.Number,
                    hideInEdit: true,
                },
                {
                    fieldName: nameof<TestPeriodModel>((period) => period.startDate),
                    prettyName: this._translation.t("testperiodManager.colStartDate"),
                    fieldType: GenericListFieldType.Date,
                    required: true,
                    editorWidth: "6",
                },
                {
                    fieldName: nameof<TestPeriodModel>((period) => period.endDate),
                    prettyName: this._translation.t("testperiodManager.colEndDate"),
                    fieldType: GenericListFieldType.Date,
                    required: true,
                    editorWidth: "6",
                },
                {
                    fieldName: nameof<TestPeriodModel>((period) => period.periodScore),
                    prettyName: this._translation.t("testperiodManager.colScore"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    required: false,
                    hideInEdit: true,
                    getDataMethod: () => getAssessmentScores(this.controlDataService),
                },
            ],

            preEditCheck: (row: TestPeriodModel) => {
                return row.ID !== 0;
            },
            preDeleteCheck: async (row: TestPeriodModel) => {
                return row.ID !== 0;
            },

            add: async (row: TestPeriodModel) => {
                await firstValueFrom(this.ds.store(this.controlGuid, row));
                this.reload.emit();
                return true;
            },
            edit: async (row: TestPeriodModel) => {
                await firstValueFrom(this.ds.store(this.controlGuid, row));
                this.reload.emit();
                return true;
            },

            delete: async (row: TestPeriodModel) => {
                await firstValueFrom(this.ds.deletePeriod(this.controlGuid, row.ID));
                this.reload.emit();
                return true;
            },

            dataMethod: () => this.ds.getTestPeriodData(this.controlGuid),

            extraButtons: [
                {
                    text: this._translation.t("testperiodManager.scoreTestPeriodBtn"),
                    show: this.controlPermissions.CanScoreEffectivenessesTestPeriod,
                    class: "btn btn-warning",
                    action: async (activeRow) => {
                        if (!activeRow || !activeRow.ID) {
                            this._toastrService.warning(
                                this._translation.t("testperiodManager.selectPeriodWarning"),
                            );
                            return false;
                        }

                        if (await this.openTestPeriodScoreDialog(activeRow.ID)) {
                            this.config.reload();
                            return true;
                        }
                        return false;
                    },
                },
            ],
        };
    }
    async openTestPeriodScoreDialog(testPeriodID: number) {
        const fields = [];
        fields.push({
            prettyName: this._translation.t("testperiodManager.conclusionLabel"),
            fieldName: "scoreId",
            fieldType: GenericListFieldType.ColoredSelect,
            placeholder: this._translation.t("testperiodManager.requiredPlaceholder"),
            required: true,
            selfInit: true,
            getDataMethod: () => getAssessmentScores(this.controlDataService),
        });

        let result = await firstValueFrom(
            this._promptService
                .prompt({
                    hasBackdrop: false,
                    data: {
                        confirmOnEnter: false,
                        closeOnEsc: false,
                        title: this._translation.t("testperiodManager.addScoreTitle"),
                        fields: fields,
                        confirmButton: {
                            text: this._translation.t("testperiodManager.okBtn"),
                        },
                    },
                })
                .getResult(),
        );
        if (result) {
            const prompt = this.getSavingPrompt();
            await firstValueFrom(this.ds.postTestPeriodScore(this.controlGuid, testPeriodID, result));
            prompt.close();
            this.reload.emit();

            return true;
        }
    }

    private getSavingPrompt(): CerrixPromptComponent {
        return this._promptService.loader(this._translation.t("testperiodManager.savingLoader"));
    }
}
